import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { usePmProperty } from '@/hooks/usePmProperty';
import { getPmAccessLogs, getGates, exportAccessLog } from '@/services/api.client';
import { AccessLogTable } from '@/components/pm/AccessLogTable';
import { AccessLogFilters } from '@/components/pm/AccessLogFilters';
import { EventDetailPanel } from '@/components/pm/EventDetailPanel';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageTitle } from '@/components/ui/page-title';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { PmAccessLogEntry, PmAccessLogFilters } from '@/types';

const DEFAULT_FILTERS: PmAccessLogFilters = { limit: 50 };

/** A filter is "active" when the user has narrowed beyond the default page size. */
function hasActiveFilters(filters: PmAccessLogFilters): boolean {
  return (
    !!filters.gateIds?.length ||
    !!filters.statuses?.length ||
    !!filters.invitationTypes?.length ||
    !!filters.visitorName ||
    !!filters.unit ||
    !!filters.guardName ||
    !!filters.dateFrom ||
    !!filters.dateTo
  );
}

export function AccessLogPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const [filters, setFilters] = useState<PmAccessLogFilters>(DEFAULT_FILTERS);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [selected, setSelected] = useState<PmAccessLogEntry | null>(null);
  const [liveOnly, setLiveOnly] = useState(false);

  const currentCursor = cursorStack[cursorStack.length - 1];

  const logsQuery = useQuery({
    queryKey: ['pm', 'access-logs', propertyId, filters, currentCursor, liveOnly],
    queryFn: () =>
      getPmAccessLogs(propertyId, {
        ...filters,
        ...(currentCursor !== undefined ? { cursor: currentCursor } : {}),
        ...(liveOnly ? { checkedInOnly: true } : {}),
      }),
    enabled: !!propertyId,
  });

  const gatesQuery = useQuery({
    queryKey: ['pm', 'gates', propertyId],
    queryFn: () => getGates(propertyId),
    enabled: !!propertyId,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      exportAccessLog(propertyId, {
        format: 'csv',
        dateFrom:
          filters.dateFrom ?? new Date(Date.now() - 7 * 86400_000).toISOString().slice(0, 10),
        dateTo: filters.dateTo ?? new Date().toISOString().slice(0, 10),
        ...(filters.gateIds?.length ? { gateIds: filters.gateIds } : {}),
        ...(filters.statuses?.length ? { statuses: filters.statuses } : {}),
        ...(filters.invitationTypes?.length ? { invitationTypes: filters.invitationTypes } : {}),
        ...(filters.visitorName ? { visitorName: filters.visitorName } : {}),
        ...(filters.unit ? { unit: filters.unit } : {}),
        ...(filters.guardName ? { guardName: filters.guardName } : {}),
      }),
    onSuccess: (job) => {
      if (job.downloadUrl) {
        window.open(job.downloadUrl, '_blank');
      } else {
        toast.success('Export queued — you will receive an email when the download is ready.');
      }
    },
    onError: () => {
      toast.error('Export failed. Please try again.');
    },
  });

  const meta = logsQuery.data?.meta;

  function handleFilterChange(next: PmAccessLogFilters) {
    setCursorStack([]);
    setFilters(next);
  }

  function handleNext() {
    const next = meta?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  }

  function handlePrev() {
    setCursorStack((s) => s.slice(0, -1));
  }

  return (
    <div className="space-y-4">
      <PageTitle
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        }
      >
        Access Log
      </PageTitle>

      <div className="flex items-center gap-2">
        <Switch
          id="live-view-toggle"
          checked={liveOnly}
          onCheckedChange={(checked) => {
            setLiveOnly(checked);
            setCursorStack([]);
          }}
        />
        <Label htmlFor="live-view-toggle" className="text-sm">
          Live view
        </Label>
      </div>

      <AccessLogFilters
        gates={gatesQuery.data ?? []}
        filters={filters}
        onChange={handleFilterChange}
        onReset={() => handleFilterChange(DEFAULT_FILTERS)}
      />

      <QueryStateBoundary
        query={logsQuery}
        isEmpty={(d) => (d?.data ?? []).length === 0}
        errorTitle="Couldn't load the access log"
        loadingFallback={<AccessLogTable entries={[]} loading onRowClick={setSelected} />}
        emptyFallback={
          hasActiveFilters(filters) ? (
            <EmptyState
              title="No access events match these filters"
              description="Try widening the date range or clearing a filter."
              variant="filtered"
              action={{
                label: 'Reset filters',
                onClick: () => handleFilterChange(DEFAULT_FILTERS),
              }}
            />
          ) : (
            <EmptyState
              title="No access events yet"
              description="Entries appear here as visitors are checked in at the gate."
            />
          )
        }
      >
        {(data) => (
          <>
            <AccessLogTable entries={data.data} loading={false} onRowClick={setSelected} />
            <div className="flex items-center justify-between text-sm text-[var(--text-tertiary)] mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                disabled={cursorStack.length === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={!data.meta?.hasMore}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </QueryStateBoundary>

      {selected && <EventDetailPanel entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
